import { v4 } from 'uuid';

import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { UiHelpersService } from '../../services';

const TYPE_LIST = {
  img: 'image/*',
  pdf: '.pdf',
  txt: 'text/plain',
  xls: '.csv, .xls, .xlsx',
  csv: '.csv',
} as const;

@Component({
  selector: 'phoenix-upload-file',
  templateUrl: './upload-file.component.html',
  standalone: true,
  imports: [MatIconModule, MatProgressSpinnerModule],
})
export class UploadFileComponent implements OnInit {
  /**
   * Array of allowed file types (MIME types, extensions, or filename patterns).
   * Example: ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', '.csv', '.xlsx', /report/i]
   */
  @Input() allowedFileTypes: (string | RegExp)[] = [];
  @Input() allowedType: keyof typeof TYPE_LIST | '' = '';
  @Input() loading = false;

  selectedAllowedType = '';
  _uploadedFiles: File[] = [];
  uiHelper = inject(UiHelpersService);
  destroyRef = inject(DestroyRef);

  @Input() aria = '';
  @Input() control: FormControl<File | null> | null = null;
  @Input() controlList = new FormControl<File[] | null>(null);

  @Input() maxFiles: number | undefined = undefined; // If undefined, no limit on the number of files
  @Input() inputId = `file-upload-${v4()}`;
  @Input() showMedia = true;
  @Output() fileList = new EventEmitter<File[]>();
  @ViewChild('fileInput')
  private readonly fileInput?: ElementRef<HTMLInputElement>;

  get uploadedFiles(): File[] {
    return this._uploadedFiles;
  }

  get uploadDisabled(): boolean {
    return (
      !!this.control?.disabled ||
      this.controlList.disabled ||
      (this.maxFiles !== undefined &&
        this._uploadedFiles.length >= this.maxFiles)
    );
  }

  ngOnInit() {
    if (this.control && this.maxFiles === undefined) {
      this.maxFiles = 1;
    }

    if (this.allowedType && TYPE_LIST[this.allowedType]) {
      this.selectedAllowedType = TYPE_LIST[this.allowedType];
    }

    // Merge allowedFileTypes strings into selectedAllowedType for the 'accept' attribute
    if (this.allowedFileTypes.length > 0) {
      const explicitTypes = this.allowedFileTypes
        .filter(
          (p): p is string =>
            typeof p === 'string' && (p.startsWith('.') || p.includes('/'))
        )
        .join(',');

      if (explicitTypes) {
        this.selectedAllowedType = this.selectedAllowedType
          ? `${this.selectedAllowedType},${explicitTypes}`
          : explicitTypes;
      }
    }

    if (this.control) {
      this.control.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(value => {
          if (value instanceof File) {
            this._uploadedFiles = [value];
          } else if (value === null) {
            this._uploadedFiles = [];
            this.clearFileInput();
          }
        });
    }

    if (this.controlList) {
      this.controlList.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(value => {
          if (Array.isArray(value)) {
            this._uploadedFiles = [...value];
            if (value.length === 0) {
              this.clearFileInput();
            }
          } else if (value === null) {
            this._uploadedFiles = [];
            this.clearFileInput();
          }
        });
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    const filesArray: File[] = Array.from(input.files);
    // Validate file types/names if allowedFileTypes is set
    if (this.allowedFileTypes && this.allowedFileTypes.length > 0) {
      const invalidFiles = filesArray.filter(file => !this.isAllowedFile(file));
      if (invalidFiles.length > 0) {
        this.uiHelper.errorSnackbar(
          `Invalid file type or name: ${invalidFiles.map(f => f.name).join(', ')}.`
        );
        this.clearFileInput(input);
        return;
      }
    }
    const availableSlots =
      this.maxFiles === undefined
        ? filesArray.length
        : this.maxFiles - this._uploadedFiles.length;
    if (availableSlots <= 0) {
      this.uiHelper.errorSnackbar(
        `You can only upload ${this.maxFiles} files.`
      );
      this.clearFileInput(input);
      return;
    }
    const filesToAdd: File[] = filesArray.slice(0, availableSlots);
    this._uploadedFiles.push(...filesToAdd);

    this.updateControl();
    this.fileList.emit(this._uploadedFiles);
  }

  private updateControl() {
    if (this.control) {
      this.control.setValue(
        this._uploadedFiles.length > 0 ? this._uploadedFiles[0] : null,
        { emitEvent: false }
      );
      this.control.markAsDirty();
    }

    if (this.controlList) {
      this.controlList.setValue(
        this._uploadedFiles.length > 0 ? [...this._uploadedFiles] : [],
        { emitEvent: false }
      );
      this.controlList.markAsDirty();
    }
  }

  truncateFilename(filename: string, maxLength: number): string {
    const lastDotIndex = filename.lastIndexOf('.');

    // Check if filename has an extension. We ignore leading dots (e.g., .gitignore)
    const hasExtension = lastDotIndex > 0;

    if (!hasExtension) {
      if (filename.length <= maxLength) return filename;
      return filename.substring(0, maxLength) + '...';
    }

    const base = filename.substring(0, lastDotIndex);
    const ext = filename.substring(lastDotIndex + 1);

    if (base.length <= maxLength) {
      return filename;
    }

    return base.substring(0, maxLength) + '...' + ext;
  }

  formatFileSize(sizeInBytes: number) {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let index = 0;

    while (sizeInBytes >= 1024 && index < units.length - 1) {
      sizeInBytes /= 1024;
      index++;
    }

    return `${sizeInBytes.toFixed(2)} ${units[index]}`;
  }

  removeFile(index: number, fileInput?: HTMLInputElement) {
    this._uploadedFiles.splice(index, 1);

    this.updateControl();
    this.fileList.emit(this._uploadedFiles);

    // Reset file input so user can re-upload the same file
    if (fileInput) {
      this.clearFileInput(fileInput);
    }
  }

  private clearFileInput(fileInput = this.fileInput?.nativeElement) {
    if (fileInput) {
      fileInput.value = '';
    }
  }

  /**
   * Checks if a file matches any of the allowedFileTypes patterns.
   */
  isAllowedFile(file: File): boolean {
    const preset = this.allowedType
      ? TYPE_LIST[this.allowedType].split(',').map(s => s.trim())
      : [];
    const combined = [...this.allowedFileTypes, ...preset];

    if (combined.length === 0) return true;

    return combined.some(pattern => {
      if (typeof pattern === 'string') {
        const lower = pattern.toLowerCase();

        // Handle wildcard MIME types like 'image/*'
        if (lower.endsWith('/*')) {
          const mimePrefix = lower.split('/')[0];
          return file.type.toLowerCase().startsWith(mimePrefix + '/');
        }

        return (
          file.type.toLowerCase().includes(lower) ||
          file.name.toLowerCase().endsWith(lower) ||
          file.name.toLowerCase().includes(lower)
        );
      }
      return pattern instanceof RegExp && pattern.test(file.name);
    });
  }
}
